import { Suspense } from 'react';

import { HorizontalContainer, Spinner, VerticalContainer } from '@/components';

import { useImageViewer } from '../lib/useImageViewer';
import { MAX_ZOOM, MIN_ZOOM } from '../lib/viewReducer';
import { SuspendedImage } from './SuspendedImage';
import { ZoomControls } from './ZoomControls';

type ImageViewerProps = {
    src: string;
    alt: string;
    rotation: number;
};

export const ImageViewer = ({ src, alt, rotation }: ImageViewerProps) => {
    const {
        zoom,
        panX,
        panY,
        containerRef,
        handleMouseDown,
        handleMouseMove,
        stopDrag,
        zoomIn,
        zoomOut,
    } = useImageViewer(src);

    // For sideways rotations (90°/270°) the image's layout width becomes the
    // visual height and vice versa. We constrain both pre-rotation axes to 75vh
    // so the rotated visual always fits inside the fixed-height container.
    const effectiveRotation = ((rotation % 360) + 360) % 360;
    const isSideways = effectiveRotation === 90 || effectiveRotation === 270;

    // All transforms are applied in this order:
    //   1. translate(pan) — moves in screen space (outermost = screen-space)
    //   2. rotate(R)      — spins from element centre
    //   3. scale(zoom)    — grows/shrinks from element centre
    const imageStyle: React.CSSProperties = {
        maxWidth: isSideways ? '75vh' : '100%',
        maxHeight: '75vh',
        width: 'auto',
        height: 'auto',
        transform: `translate(${panX}px, ${panY}px) rotate(${rotation}deg) scale(${zoom})`,
        userSelect: 'none',
    };

    return (
        <VerticalContainer spacing="none" className="items-center gap-3">
            {/* Fixed-height container — overflow-hidden so nothing ever spills
                out regardless of rotation angle or zoom level. */}
            <div
                ref={containerRef}
                className="flex h-[75vh] w-full items-center justify-center overflow-hidden"
                style={{ cursor: zoom > 1 ? 'grab' : 'default' }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={stopDrag}
                onMouseLeave={stopDrag}
            >
                <Suspense
                    fallback={
                        <HorizontalContainer
                            spacing="none"
                            className="h-48 items-center justify-center"
                        >
                            <Spinner className="text-white" />
                        </HorizontalContainer>
                    }
                >
                    <SuspendedImage
                        src={src}
                        alt={alt}
                        imageStyle={imageStyle}
                    />
                </Suspense>
            </div>
            <ZoomControls
                zoom={zoom}
                minZoom={MIN_ZOOM}
                maxZoom={MAX_ZOOM}
                onZoomIn={zoomIn}
                onZoomOut={zoomOut}
            />
        </VerticalContainer>
    );
};
